import { useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { TextAssist } from "@/components/TextAssist";
import { Loader2, Sparkles, Wand2, FileText, Paperclip, Camera, ShieldOff } from "lucide-react";
import { V } from "@/lib/vocab";

export const ATTACH_ACCEPT = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.xlsm,.csv,.txt,.md,.rtf,.odt,.ods,.ppt,.pptx,.pages,.numbers,.dwg,.dxf,.zip";
export const READ_ACCEPT = "image/*,.pdf,.docx,.xlsx,.xls,.pptx,.txt,.md,.csv";
const BAD = (x) => /\.(svg|svgz|html?|xhtml|js|mjs)$/i.test(x.name);

/* Pick files off the disk with the same rules everywhere: ≤10 MB, no scripts/markup. */
export function pickScopeFiles(list) {
  const picked = Array.from(list || []);
  const ok = picked.filter((x) => x.size > 0 && x.size <= 10 * 1024 * 1024 && !BAD(x));
  picked.filter((x) => !ok.includes(x)).forEach((x) => toast.error(
    BAD(x) ? `${x.name} isn't a document we can share — use a PDF, doc or photo` : `${x.name} is ${x.size ? "over 10 MB" : "empty"}`));
  return ok;
}

/* The scope-of-work editor, one for jobs and projects (iter372): write it, dictate it, let AI
   draft it from the title/notes, read it out of a document you already have, attach the PDF
   written outside the app or a photo of the problem. `pending`/`onPending` collect files in a
   wizard (uploaded after the record exists); `onUpload` uploads live on an existing record. */
export function ScopeEditor({
  tenantId, value, onChange, kind = "job", draftCtx = {}, readOnly,
  pending, onPending, onUpload, share, onShare,
  testid = "scope", textareaTestId, rows,
}) {
  const [drafting, setDrafting] = useState(false);
  const [reading, setReading] = useState(false);
  const [source, setSource] = useState(null);
  const srcRef = useRef(null);
  const fileRef = useRef(null);
  const v = value || "";

  const draft = async (src) => {
    const doc = src || source;
    if (!(draftCtx.title || "").trim()) return toast.error(`Give the ${kind} a title first — that's what we draft from.`);
    setDrafting(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/scope-draft`, {
        kind, title: draftCtx.title, category: draftCtx.category, location: draftCtx.location, priority: draftCtx.priority,
        budget: draftCtx.budget, bids_due: draftCtx.bids_due, project_id: draftCtx.project_id,
        notes: doc ? "" : (draftCtx.notes || v), source_text: doc?.text || "", source_name: doc?.name || "",
      });
      onChange(data.scope);
      toast.success(doc ? `Drafted from ${doc.name} — edit anything before it goes out` : "Draft ready — edit anything before it goes out");
    } catch (e) { toast.error(errMsg(e, "Could not draft that")); }
    setDrafting(false);
  };

  const read = async (file) => {
    if (!file) return;
    setReading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/scope-read`, fd);
      setSource(data);
      await draft(data);
    } catch (e) { toast.error(errMsg(e, "Couldn't read that document")); }
    setReading(false);
    if (srcRef.current) srcRef.current.value = "";
  };

  const addFiles = async (list) => {
    const ok = pickScopeFiles(list);
    if (fileRef.current) fileRef.current.value = "";
    if (!ok.length) return;
    if (onUpload) { for (const f of ok) await onUpload(f); }
    else onPending?.([...(pending || []), ...ok]);
  };

  const busy = drafting || reading;
  return (
    <div data-testid={`${testid}-block`} className="space-y-3">
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-1.5">
          <TextAssist value={v} onChange={onChange} surface="scope" testId={`${testid}-assist`} micTitle="Dictate the scope" align="left" />
          <input ref={srcRef} type="file" className="hidden" data-testid={`${testid}-source-input`} accept={READ_ACCEPT}
            onChange={(e) => read(e.target.files?.[0])} />
          <button type="button" data-testid={`${testid}-read`} onClick={() => srcRef.current?.click()} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/65 hover:border-ink/40 disabled:opacity-50">
            {reading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
            {reading ? "Reading…" : "Draft from a document"}
          </button>
          <button type="button" data-testid={`${testid}-ai`} onClick={() => draft()} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-xl bg-ink px-3 py-1.5 text-xs font-bold text-white hover:bg-ink/85 disabled:opacity-50">
            {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {drafting ? "Drafting…" : v ? "Redraft with AI" : "Draft with AI"}
          </button>
        </div>
      )}
      {source && (
        <div data-testid={`${testid}-source`} className="flex flex-wrap items-center gap-2 rounded-lg bg-sand-50 px-3 py-2 text-xs font-semibold text-ink/60">
          <FileText className="h-3.5 w-3.5" /> Drafting from {source.name} · {Number(source.words || 0).toLocaleString()} words read
          <button type="button" data-testid={`${testid}-source-clear`} onClick={() => setSource(null)} className="text-ink/35 hover:text-redline-600">×</button>
        </div>
      )}
      <textarea data-testid={textareaTestId || `${testid}-text`} value={v} readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)} rows={rows || (v ? 14 : 6)}
        placeholder={kind === "job"
          ? "What exactly, where, what's included and what isn't, access, how you'll know it's done. Or just draft it with AI from the title."
          : "What's included, what isn't, access and hours, what the bid must cover."}
        className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 font-mono text-xs leading-relaxed outline-none focus:ring-2 focus:ring-redline-500/30" />

      {(onPending || onUpload) && !readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" className="hidden" data-testid={`${testid}-file-input`} multiple accept={ATTACH_ACCEPT}
            onChange={(e) => addFiles(e.target.files)} />
          <button type="button" data-testid={`${testid}-file-btn`} onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/60 hover:border-ink/40">
            <Paperclip className="h-3.5 w-3.5" /> Attach a PDF or document
          </button>
          <button type="button" data-testid={`${testid}-photo-btn`} onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/60 hover:border-ink/40">
            <Camera className="h-3.5 w-3.5" /> Add a photo
          </button>
          {(pending || []).map((x, i) => (
            <span key={i} data-testid={`${testid}-file-${i}`} className="inline-flex items-center gap-1.5 rounded-lg bg-sand-100 px-2.5 py-1 text-xs font-semibold text-ink/65">
              <FileText className="h-3 w-3" /> {x.name}
              <button type="button" onClick={() => onPending(pending.filter((_, j) => j !== i))} className="text-ink/35 hover:text-redline-600">×</button>
            </span>
          ))}
        </div>
      )}

      {onShare && (
        <ScopeShare kind={kind} share={share} onChange={onShare} readOnly={readOnly} testid={`${testid}-share`} />
      )}
      <p className="flex items-start gap-1.5 text-xs text-ink/45" data-testid={`${testid}-resident-note`}>
        <ShieldOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {V.many("person")} never see a scope. It goes to the company{kind === "project" ? " bidding" : ""} and to the people you copy — as a PDF, not a link.
      </p>
    </div>
  );
}

/* Who the scope travels to. A project's scope always goes to the bidders — that's the point. */
export function ScopeShare({ kind, share = {}, onChange, readOnly, testid = "scope-share" }) {
  const vendor = share.vendor !== false;
  const cc = share.cc !== false;
  return (
    <div data-testid={testid} className="flex flex-wrap gap-2">
      {kind !== "project" && (
        <label className={`inline-flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${vendor ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-sand-200 bg-white text-ink/55"}`}>
          <input type="checkbox" data-testid={`${testid}-vendor`} data-state={vendor ? "checked" : "unchecked"} checked={vendor} disabled={readOnly}
            onChange={(e) => onChange({ ...share, vendor: e.target.checked })} />
          Send to the company (crew page, office, invite)
        </label>
      )}
      <label className={`inline-flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${cc ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-sand-200 bg-white text-ink/55"}`}>
        <input type="checkbox" data-testid={`${testid}-cc`} data-state={cc ? "checked" : "unchecked"} checked={cc} disabled={readOnly}
          onChange={(e) => onChange({ ...share, cc: e.target.checked })} />
        Attach to the emails the CC list gets (scheduled, award, contract, invoices)
      </label>
    </div>
  );
}
